import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchAttachmentPreviewUrl, previewKindOf, type PreviewKind } from './attachmentPreview'

/** Açık önizleme. `url` bir object URL'dir ve kapanışta serbest bırakılır. */
export interface AttachmentPreviewState {
  url: string
  kind: PreviewKind
  fileName: string
}

/** Önizlenecek satırın hook'un ihtiyaç duyduğu **asgari** şekli (`AttachmentTableRow` bunu karşılar). */
export interface AttachmentPreviewSource {
  fileName: string
  extension: string
  downloadId?: number
}

interface Options {
  /**
   * Hangi kayıt için açıldığını söyleyen kimlik; pop-up kapalıyken `null`. Değeri değişince açık
   * önizleme kapanır ve bekleyen bir indirme **atılır** (aşağıdaki yarış koruması).
   */
  scopeId: number | null
  /** İndirme başarısız olduğunda çağrılır — mesaj metni çağıran ekrana aittir. */
  onError: () => void
}

/**
 * Dosya ekinin **pop-up içinde** önizlenmesi (108) — durum, object URL ömrü ve yarış koruması.
 *
 * <p><b>Neden ortak:</b> aynı davranış iki pop-up'ta gerekiyor (Prensip Kararları — Dosyalar ve
 * Toplantı Yönetimi — Görev Dosyaları) ve kopyalanacak olan şey, geçmişte bir push kapısı bulgusuyla
 * düzeltilmiş <b>ince</b> bir mantıktır (aşağıdaki `openedFor` karşılaştırması). İkinci bir kopya,
 * aynı hatayı sessizce geri getirirdi.
 *
 * <h2>Yarış koruması</h2>
 *
 * İndirme sürerken pop-up kapatılıp <b>başka</b> bir kayıt açılabilir; o zaman gelen blob artık
 * yanlış kayda aittir. Hangi kayıt için açıldığı kilitlenir ve dönüşte güncel değerle karşılaştırılır
 * — aksi hâlde yeni pop-up, kendi listesi yerine önceki kaydın dosyasını gösteriyordu.
 *
 * <p>Güncel değer <b>ref'ten</b> okunur: `open` kendi closure'ındaki `scopeId`'yi görür ve bekleyen
 * bir indirme dönerken o değer bayat olurdu.
 */
export function useAttachmentPreview({ scopeId, onError }: Options) {
  const scopeIdRef = useRef(scopeId)
  scopeIdRef.current = scopeId

  /**
   * Bileşen hâlâ ekranda mı? {@code scopeId} sökülme anında <b>değişmez</b>, dolayısıyla aşağıdaki
   * kapsam karşılaştırması sökülmeyi yakalamaz: bekleyen indirme dönerse blob üretilir, {@code
   * setPreview} etkisiz kalır ve object URL sekme ömrü boyunca sızardı.
   */
  const aliveRef = useRef(true)
  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
    }
  }, [])

  const [preview, setPreview] = useState<AttachmentPreviewState | null>(null)
  const [loading, setLoading] = useState(false)

  const close = useCallback(() => {
    setPreview((current) => {
      if (current) URL.revokeObjectURL(current.url)
      return null
    })
  }, [])

  // Pop-up kapanınca (ya da başka bir kayda geçilince) açık önizleme de kapanır: aksi halde bir
  // sonraki açılışta ÖNCEKİ kaydın dosyası görünürdü (durum bileşenle birlikte yaşıyor) ve object URL
  // sekme ömrü boyunca sızardı.
  useEffect(() => {
    if (scopeId == null) close()
  }, [scopeId, close])

  // Bileşen sökülürse (ör. sayfadan çıkış) açık object URL serbest bırakılır — yukarıdaki efekt
  // yalnız pop-up KAPANIŞINI yakalar, sökülmeyi değil.
  useEffect(() => close, [close])

  const open = useCallback(
    async (row: AttachmentPreviewSource) => {
      const kind = previewKindOf(row.extension)
      if (kind == null || row.downloadId == null) return
      const openedFor = scopeIdRef.current
      setLoading(true)
      try {
        const url = await fetchAttachmentPreviewUrl(row.downloadId, row.extension)
        // Sökülme de kapsam değişikliği gibi "artık gerekmiyor" demektir; ikisi de blob'u atar.
        if (!aliveRef.current || openedFor !== scopeIdRef.current) {
          URL.revokeObjectURL(url)
          return
        }
        close()
        setPreview({ url, kind, fileName: row.fileName })
      } catch {
        onError()
      } finally {
        setLoading(false)
      }
    },
    [close, onError],
  )

  return { preview, loading, open, close }
}
